import * as Sentry from '@sentry/react'
import version from './VERSION.json'
import { beforeBreadcrumb, beforeSend } from './sentry-filters'

export interface ConfigFile {
    ENV: string
    API_URL: string
    UPLOAD_URL: string
    CLIENT_REQUEST_UPLOAD_URL: string
    PAYMENT_URL: string
    RATES_URL: string
    CAPTCHA_URL: string
    WITHDRAWAL_RECEIPT_URL: string
    CLIENT_STATEMENT_URL: string
    WTR_URL: string
    TRADE_STATEMENT_CSV_URL: string
    FA2_URL: string
    AUTH_URL: string
    PAMM_PORTAL: string
    ADDRESS_LOOKUP_URL: string
    EXCHANGE_RATES_URL: string
    CORPORATE_URL: string
    PULL_NOTIFICATIONS_SEC: number
    PULL_APP_STATUS_SEC: number
    DEFAULT_LANGUAGE: string
    VERIFICATION_ATTEMPTS: number
    VERIFICATION_INTERVAL_SEC: number
    LOGOUT_AFTER_MIN: number
    REACT_APP_RECAPTCHA_SITE_KEY_V3: string
    REACT_APP_HCAPTCHA_KEY: string
    CORPORATE?: boolean
    SIGNALS_URL: string
    IB_INVOICE_URL: string,
    // THPRT-6468: host of the Kong rewards gateway. The '/rewards/v1' prefix is added
    // in code. Empty on dev, where the call is served through the setupProxy proxy.
    REWARDS_API_URL: string
    BROKEREE_URL: string
    SENTRY_DSN: string
    KEYCLOAK_URL: string
    KEYCLOAK_REALM: string
    KEYCLOAK_CLIENT_ID: string
    // TMJV-968: ThinkCopy T&C acceptance recording endpoint; defaults false until BE is ready.
    THINKCOPY_TNC_RECORDING_ENABLED: boolean
    // THPRT-6264: multi demo account activation flow; BE dependency TMJV-1001.
    MULTI_DEMO_ACCOUNTS_ENABLED: boolean
    // THPRT-6264: serve fixture responses for the new demo activation endpoints
    // while TMJV-1001 is not deployed. Only ever true via config.dev.json.
    DEMO_ACTIVATION_MOCK: boolean
    // TMJV-144: prepaid branded cards. Non-production only until the copy has
    // Compliance approval and the Gate2Pay production key exists.
    COMPETITION_API_URL?: string
    // TEMPORARY, THPRT-6408: stands in for the terms version the competition module
    // does not yet serve; /competition returns terms: null in every environment,
    // including production. Now set in production and hk as well as uat and dev,
    // because without it registration fails with a 400 on path ["terms","version"].
    // Remove in all four the moment the module serves a real version.
    COMPETITION_TERMS_VERSION?: string
}

let appConfig: ConfigFile
// The portal is served per market from a `portal.<domain>` host out of one build.
// Each mapped host loads its own config; every other host (including thinkmarkets.com)
// uses the baked config.json. Production only: dev always loads config.dev.json.
const CONFIG_BY_HOST: Record<string, string> = {
    'portal.thinkvina.com': 'config.vina.json',
    'portal-uat.thinkvina.com': 'config.vina-uat.json',
}
const host = typeof window !== 'undefined' ? window.location.hostname : ''
const configFileName = process.env.NODE_ENV === 'production'
    ? (CONFIG_BY_HOST[host] ?? 'config.json')
    : 'config.dev.json'

const appInit = (() => {
    return fetch(`/${configFileName}?v=${new Date().getTime()}`)
        .then(response => response.json())
        .then((response: ConfigFile) => {
            appConfig = {
                ...response,
                AUTH_URL: response.AUTH_URL,
                API_URL: response.API_URL + '/nsdata',
                UPLOAD_URL: response.API_URL + '/upload',
                CLIENT_REQUEST_UPLOAD_URL: response.API_URL + '/supportRequestFileUpload',
                PAYMENT_URL: response.API_URL + '/payment',
                CAPTCHA_URL: response.API_URL + '/captcha',
                RATES_URL: response.RATES_URL + '/exchangerates',
                WITHDRAWAL_RECEIPT_URL: response.API_URL + '/withdrawalReceipt',
                IB_INVOICE_URL: response.API_URL + '/invoiceDownload',
                CLIENT_STATEMENT_URL: response.API_URL + '/clientstatement',
                TRADE_STATEMENT_CSV_URL: response.API_URL + '/tradeStatementCsv',
                BROKEREE_URL: response.BROKEREE_URL,
                PAMM_PORTAL: response.PAMM_PORTAL,
                // THPRT-6273 / TMJV-968: ThinkCopy T&C recording (Part B). Enabled on non-prod
                // (dev/uat/staging) plus production ('ld') following Compliance sign-off.
                THINKCOPY_TNC_RECORDING_ENABLED: ['dev', 'uat', 'staging', 'ld', 'vina'].includes(response.ENV),
                // THPRT-6264: multi demo accounts flow. Enabled in production ('ld')
                // in 3.12.25.1 after the 3.12.25 UAT verification and team sign-off.
                MULTI_DEMO_ACCOUNTS_ENABLED: ['dev', 'uat', 'staging', 'ld', 'vina'].includes(response.ENV),
                DEMO_ACTIVATION_MOCK: response.DEMO_ACTIVATION_MOCK === true,
                COMPETITION_API_URL: response.COMPETITION_API_URL || '',
                COMPETITION_TERMS_VERSION: response.COMPETITION_TERMS_VERSION || '',
                REWARDS_API_URL: response.REWARDS_API_URL || ''
            }
            if (process.env.NODE_ENV === 'production' && appConfig.ENV === 'dev')
                throw new Error('Development configuration file in production build')
            if (appConfig.SENTRY_DSN) {
                Sentry.init({
                    dsn: appConfig.SENTRY_DSN,
                    environment: appConfig.ENV,
                    release: `portal-2.0@${version.version}.${version.build}`,
                    sendDefaultPii: false,
                    maxBreadcrumbs: 30,
                    integrations: [
                        Sentry.browserTracingIntegration(),
                    ],
                    tracePropagationTargets: [],
                    tracesSampleRate: appConfig.ENV === 'uat' ? 0.1 : 0.01,
                    beforeBreadcrumb,
                    beforeSend,
                })
            }
            return appConfig
        })
        .catch(e => {
            const m = `Error fetching configuration from server\n${e.message}`
            throw new Error(m)
        })
})()

export { appConfig, appInit }